import React, { useState, useRef } from 'react';
import BordPhoto from '../../BordPhoto/BordPhoto';
import BordResponsibleUserHoverDropdown from '../BordResponsibleUserHoverDropdown/BordResponsibleUserHoverDropdown';
import { IResponsibleUserData } from '../../../../types/responsibleUser.types';
import './BordResponsibleUserPictures.scss';

/**
 * Muestra hasta 3 usuarios responsables con sus fotos y un contador adicional si hay más.
 * Al hacer hover, si hay más de 3, muestra un dropdown con todos los usuarios.
 * @param responsibleUsersList Lista de usuarios responsables a mostrar.
 * @param className Clase personalizada opcional.
 * @param hideDropdown Si se debe ocultar el dropdown.
 * @param dropdownDirection Dirección del dropdown: 'left' para posicionar hacia la izquierda, 'right' para la derecha (por defecto).
 */
const BordResponsibleUserPictures: React.FC<BordResponsibleUserPicturesProps> = ({
  responsibleUsersList,
  className = '',
  hideDropdown,
  dropdownDirection = 'right'
}) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const maxToShow = 3;
  const showUsers = responsibleUsersList?.slice(0, maxToShow);
  const extraCount = responsibleUsersList?.length ? Math.max(0, responsibleUsersList?.length - maxToShow) : 0;
  const displayDropdown = showDropdown && !hideDropdown;

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowDropdown(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowDropdown(false);
    }, 150);
  };

  const handleDropdownMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleDropdownMouseLeave = () => setShowDropdown(false);

  return (
    <div className="bordResponsibleUserPicturesWrapper">
      <div
        className={`bordResponsibleUserPictures ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {showUsers?.map((user, index) => (
          <div key={`${user.id}-${index}`} className={`photoWrapper${index > 0 ? ' photoWrapperOverlap' : ''}`}>
            <BordPhoto
              url={user?.user?.photoProfile}
              variant="circle"
              size="s24"
              avatarProps={{
                variant: 'circularUser',
                standardSize: 24
              }}
            />
          </div>
        ))}

        {!!extraCount && <span className="extraCount">+{extraCount}</span>}
      </div>

      {displayDropdown && (
        <div
          className={`bordResponsibleUserPicturesDropdown ${
            dropdownDirection === 'left' ? 'dropdownLeft' : 'dropdownRight'
          }`}
          onMouseEnter={handleDropdownMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
        >
          <BordResponsibleUserHoverDropdown responsibleUsersList={responsibleUsersList || []} />
        </div>
      )}
    </div>
  );
};

export default BordResponsibleUserPictures;

export interface BordResponsibleUserPicturesProps {
  responsibleUsersList: IResponsibleUserData[];
  className?: string;
  hideDropdown?: boolean;
  dropdownDirection?: 'left' | 'right';
}
